"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";

interface AccordionItem {
  title: string;
  content: React.ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
  multiExpand?: boolean;
}

export default function Accordion({ items, multiExpand = false }: AccordionProps) {
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);

  const handleToggle = (index: number) => {
    if (multiExpand) {
      if (expandedIndexes.includes(index)) {
        setExpandedIndexes(expandedIndexes.filter((i) => i !== index));
      } else {
        setExpandedIndexes([...expandedIndexes, index]);
      }
    } else {
      if (expandedIndexes.includes(index)) {
        setExpandedIndexes([]);
      } else {
        setExpandedIndexes([index]);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle(index);
    }
  };

  return (
    <div className="accordion-container">
      {items.map((item, index) => {
        const isExpanded = expandedIndexes.includes(index);
        return (
          <div key={index} className="accordion-item card">
            <button
              className="accordion-header"
              onClick={() => handleToggle(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-expanded={isExpanded}
              aria-controls={`accordion-content-${index}`}
              id={`accordion-header-${index}`}
            >
              <span className="accordion-title">{item.title}</span>
              <ChevronDown size={18} className={`accordion-icon ${isExpanded ? "open" : ""}`} />
            </button>
            
            <div
              id={`accordion-content-${index}`}
              className={`accordion-content-wrapper ${isExpanded ? "open" : ""}`}
              role="region"
              aria-labelledby={`accordion-header-${index}`}
              aria-hidden={!isExpanded}
            >
              <div className="accordion-content">
                <div>{item.content}</div>
              </div>
            </div>
          </div>
        );
      })}

      <style jsx>{`
        .accordion-container {
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
          width: 100%;
        }
        .accordion-item {
          padding: 0 !important;
          overflow: hidden;
          transition: border-color var(--transition-normal);
        }
        .accordion-header {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-4) var(--space-5);
          background: transparent;
          border: none;
          color: var(--text-primary);
          font-family: var(--font-heading);
          font-size: var(--text-md);
          font-weight: var(--fw-bold);
          text-align: left;
          cursor: pointer;
          transition: background-color var(--transition-fast);
        }
        .accordion-header:focus-visible {
          outline: 2px solid var(--primary);
          outline-offset: -2px;
        }
        .accordion-header:hover {
          background-color: var(--bg-surface-hover);
        }
        .accordion-icon {
          color: var(--text-secondary);
          transition: transform var(--transition-normal);
        }
        .accordion-icon.open {
          transform: rotate(180deg);
          color: var(--primary);
        }
        .accordion-content-wrapper {
          max-height: 0;
          overflow: hidden;
          transition: max-height var(--transition-slow) cubic-bezier(0, 1, 0, 1);
        }
        .accordion-content-wrapper.open {
          max-height: 1000px; /* Allow space for large content */
          transition: max-height var(--transition-slow) cubic-bezier(1, 0, 1, 0);
        }
        .accordion-content {
          padding: var(--space-5);
          border-top: 1px solid var(--border-color);
          background-color: var(--shadow-black-a05);
        }
        .accordion-content p {
          margin-top: 0;
          font-size: var(--text-sm);
          line-height: 1.6;
          color: var(--text-secondary);
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
}
export type { AccordionItem };
